/**
 * Field Map — the small drawing pieces: swatches, the stacked bar, the legend,
 * the KPI tile, the population curve and one field's detail.
 *
 * Every bucket is told apart three ways — a colour, a pattern laid over it and
 * a text label — so nothing on the page depends on colour alone.
 */

import { Fragment, useEffect, useRef, type ReactNode } from 'react'
import clsx from 'clsx'
import { AlertTriangle, Info } from 'lucide-react'

import type { FieldMap, FieldMapRow } from '@/services/loans'
import { formatFieldValue } from './format'
import {
  ACTOR_LABEL,
  BUCKET_ORDER,
  BUCKETS,
  RESOLUTION_LABEL,
  stamp,
  type BucketCounts,
  type BucketId,
} from './fieldMapModel'

export function Swatch({ bucket, className }: { bucket: BucketId; className?: string }) {
  const b = BUCKETS[bucket]
  return (
    <i
      aria-hidden
      className={clsx('inline-block h-2.5 w-2.5 shrink-0 rounded-[2px]', className)}
      style={{ backgroundColor: b.color, backgroundImage: b.image, backgroundSize: b.size }}
    />
  )
}

/** The page's one sentence, with every figure in it set bold so it scans as numbers first. */
export function Headline({ text }: { text: string }) {
  return (
    <p data-fieldmap-headline className="min-w-0 flex-1 pt-1 text-[13px] leading-snug text-stone-300">
      {text.split(/(\d[\d,]*(?:\.\d+)?%?)/).map((part, i) =>
        i % 2 === 1 ? <b key={i} className="font-semibold tabular-nums text-stone-50">{part}</b> : <Fragment key={i}>{part}</Fragment>,
      )}
    </p>
  )
}

/** Every bucket defined, and what the scores count: the explanation the page keeps out of its way. */
export function HowToRead({ basis }: { basis: string }) {
  const ref = useRef<HTMLDetailsElement>(null)
  useEffect(() => {
    // A <details> stays open until its summary is pressed again; a popover should not.
    const close = (e: Event) => {
      const el = ref.current
      if (!el?.open) return
      if (e.type === 'keydown') {
        if ((e as KeyboardEvent).key !== 'Escape') return
        // The dossier shell listens for Escape on window to close the whole record. This one
        // press only closes the popover, so the shell must not see it.
        e.stopPropagation()
        el.open = false
        el.querySelector('summary')?.focus()
        return
      }
      if (e.type === 'mousedown' && el.contains(e.target as Node)) return
      el.open = false
    }
    document.addEventListener('keydown', close)
    document.addEventListener('mousedown', close)
    return () => {
      document.removeEventListener('keydown', close)
      document.removeEventListener('mousedown', close)
    }
  }, [])
  return (
    <details ref={ref} className="relative">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 rounded border border-white/10 px-2.5 py-1.5 text-[10px] uppercase tracking-wider text-stone-400 hover:text-stone-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-300 [&::-webkit-details-marker]:hidden">
        <Info size={11} aria-hidden /> How to read this
      </summary>
      <div className="absolute right-0 top-full z-30 mt-1 w-[min(28rem,86vw)] space-y-2 rounded-md border border-amber-400/20 bg-stone-950 p-3 text-[11px] shadow-xl">
        <ul className="space-y-1.5">
          {BUCKET_ORDER.map((id) => (
            <li key={id} className="grid grid-cols-[0.75rem_minmax(0,1fr)] gap-x-2">
              <Swatch bucket={id} className="mt-0.5" />
              <span>
                <span className="font-medium text-stone-100">{BUCKETS[id].label}.</span>{' '}
                <span className="text-stone-400">{BUCKETS[id].hint}</span>
              </span>
            </li>
          ))}
        </ul>
        <p className="border-t border-white/[0.07] pt-2 text-stone-500">{basis}</p>
      </div>
    </details>
  )
}

/** The status cell: the bucket's swatch and its name, never the swatch alone. */
export function StatusChip({ bucket }: { bucket: BucketId }) {
  const b = BUCKETS[bucket]
  return (
    <span title={b.hint} className="inline-flex items-center gap-1.5 whitespace-nowrap text-[10px] text-stone-300">
      <Swatch bucket={bucket} />
      {b.short}
    </span>
  )
}

export function Chip({ children, tone = 'stone', title }: { children: ReactNode; tone?: 'stone' | 'amber'; title?: string }) {
  return (
    <span
      title={title}
      className={clsx(
        'inline-flex items-center whitespace-nowrap rounded border px-1.5 py-px font-mono text-[9px] uppercase tracking-wider',
        tone === 'amber' ? 'border-amber-400/30 text-amber-300' : 'border-white/[0.08] text-stone-500',
      )}
    >
      {children}
    </span>
  )
}

export function Banner({ children, tone = 'amber' }: { children: ReactNode; tone?: 'amber' | 'stone' }) {
  return (
    <div
      role="note"
      className={clsx(
        'flex items-start gap-2 rounded border px-3 py-1.5 text-[11px]',
        tone === 'amber' ? 'border-amber-400/25 bg-amber-400/[0.05] text-amber-200' : 'border-white/[0.08] bg-white/[0.025] text-stone-400',
      )}
    >
      <AlertTriangle size={12} className="mt-0.5 shrink-0" aria-hidden />
      <div>{children}</div>
    </div>
  )
}

/**
 * One horizontal stacked bar: a segment per bucket, wide in proportion to its
 * count. A count is printed inside a segment only where it fits; the tooltip
 * and the accessible name always carry every figure.
 */
export function StackBar({ counts, total, name }: { counts: BucketCounts; total: number; name: string }) {
  const parts = BUCKET_ORDER.filter((id) => counts[id] > 0)
  const summary = parts.map((id) => `${counts[id]} ${BUCKETS[id].label.toLowerCase()}`).join(', ')
  return (
    <div
      role="img"
      aria-label={total === 0 ? `${name}: no fields` : `${name}: ${summary}`}
      className="flex h-full max-h-7 min-h-5 w-full overflow-hidden rounded bg-white/[0.05]"
    >
      {total === 0
        ? null
        : parts.map((id) => {
            const share = (100 * counts[id]) / total
            const b = BUCKETS[id]
            return (
              <div
                key={id}
                data-bucket={id}
                title={`${name} — ${b.label}: ${counts[id]} of ${total} (${share.toFixed(1)}%)`}
                className="flex min-w-px items-center justify-center overflow-hidden font-mono text-[10px] font-semibold tabular-nums"
                style={{
                  width: `${share}%`,
                  backgroundColor: b.color,
                  backgroundImage: b.image,
                  backgroundSize: b.size,
                  color: b.ink,
                }}
              >
                {share >= 7 ? (
                  // The figure sits on a plate of the segment's own colour, so a pattern never runs behind a digit.
                  <span className="rounded-sm px-1" style={{ backgroundColor: b.color }}>{counts[id]}</span>
                ) : null}
              </div>
            )
          })}
    </div>
  )
}

/** The legend doubles as the bucket filter for the field table. */
export function Legend({
  counts,
  active,
  onPick,
}: {
  counts: BucketCounts
  active: string
  onPick: (next: BucketId | '') => void
}) {
  return (
    <ul className="flex flex-wrap gap-1" aria-label="Legend. Choose one to filter the field table">
      {BUCKET_ORDER.filter((id) => id === 'shown' || counts[id] > 0).map((id) => {
        const on = active === id
        return (
          <li key={id}>
            <button
              type="button"
              aria-pressed={on}
              title={BUCKETS[id].hint}
              onClick={() => onPick(on ? '' : id)}
              className={clsx(
                'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] transition focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-300',
                on
                  ? 'border-amber-300/60 bg-amber-300/10 text-amber-200'
                  : 'border-white/[0.08] text-stone-400 hover:border-white/20 hover:text-stone-200',
              )}
            >
              <Swatch bucket={id} />
              {BUCKETS[id].short}
              <span className="font-mono tabular-nums text-stone-500" data-legend-count={id}>{counts[id]}</span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

export function Tile({
  label,
  value,
  fraction,
  aside,
  accent,
  tone,
  title,
}: {
  label: string
  value: string
  /** 0-100: draws a gauge under the figure. Omitted for plain counts. */
  fraction?: number
  /** A few words beside the figure: its share, or what the count means. */
  aside?: string
  accent: string
  tone?: string
  title?: string
}) {
  return (
    <div
      data-fieldmap-stat={label}
      title={title}
      className="relative min-w-0 overflow-hidden rounded-md border border-amber-400/15 bg-black/30 py-1.5 pl-3.5 pr-2.5"
    >
      <span aria-hidden className="absolute inset-y-0 left-0 w-[3px]" style={{ background: accent }} />
      <div className="truncate font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-stone-500">{label}</div>
      <div className="mt-0.5 flex items-baseline gap-2">
        <span className={clsx('shrink-0 text-[19px] font-semibold leading-none tabular-nums', tone ?? 'text-stone-100')}>{value}</span>
        {aside ? <span className="min-w-0 truncate text-[10px] text-stone-500">{aside}</span> : null}
      </div>
      {fraction != null ? (
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-white/[0.07]" aria-hidden>
          <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, fraction))}%`, background: accent }} />
        </div>
      ) : null}
    </div>
  )
}

const ACTOR_SWATCH = [
  { key: 'package', label: 'Read from the package', short: 'Package', fill: 'bg-amber-300/80', svg: 'fill-amber-300/80' },
  { key: 'calculated', label: 'Calculated', short: 'Calculated', fill: 'bg-stone-400/70', svg: 'fill-stone-400/70' },
  { key: 'person', label: 'Entered or confirmed by a person', short: 'Person', fill: 'bg-emerald-400/70', svg: 'fill-emerald-400/70' },
] as const

const NOT_THE_SCORE = 'A deal created before events were written holds more fields than events, so this is not the score.'

/**
 * Fields populated by recorded events, split by who put the value there. With
 * one recorded day there is no curve to draw, so the day is one stacked bar;
 * with more, a column per day.
 */
export function Curve({ points }: { points: FieldMap['population'] }) {
  const first = points[0]
  const last = points[points.length - 1]
  const legend = (
    <figcaption className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-stone-500">
      {ACTOR_SWATCH.map((a) => (
        <span key={a.key} title={a.label} className="inline-flex items-center gap-1"><i className={clsx('h-2 w-2 rounded-sm', a.fill)} /> {a.short}</span>
      ))}
    </figcaption>
  )
  if (points.length === 1) {
    return (
      <figure className="flex min-h-0 flex-1 flex-col gap-1">
        <div
          role="img"
          aria-label={`${first.populated} fields populated by recorded events on ${stamp(first.date)}`}
          className="flex max-h-12 min-h-5 flex-1 overflow-hidden rounded bg-white/[0.05]"
        >
          {ACTOR_SWATCH.map((a) =>
            first[a.key] > 0 ? (
              <div
                key={a.key}
                title={`${a.label}: ${first[a.key]} of ${first.populated}`}
                className={clsx('flex items-center justify-center font-mono text-[11px] font-semibold tabular-nums text-black/80', a.fill)}
                style={{ width: `${(100 * first[a.key]) / Math.max(1, first.populated)}%` }}
              >
                {first[a.key]}
              </div>
            ) : null,
          )}
        </div>
        <div className="text-[10px] tabular-nums text-stone-500" title={NOT_THE_SCORE}>
          {first.populated} fields on {stamp(first.date)} · recorded events, not the score
        </div>
        {legend}
      </figure>
    )
  }
  const w = 640
  const h = 96
  const max = Math.max(1, ...points.map((p) => p.populated))
  const barW = Math.min(18, Math.max(3, (w - 8) / Math.max(points.length, 1) - 2))
  const step = points.length > 1 ? (w - 8 - barW) / (points.length - 1) : 0
  const y = (n: number) => h - 14 - (n / max) * (h - 26)
  return (
    <figure className="space-y-1">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        width="100%"
        role="img"
        aria-label={`Fields populated by recorded events ${last.populated >= first.populated ? 'rose' : 'fell'} from ${first.populated} on ${stamp(first.date)} to ${last.populated} on ${stamp(last.date)}`}
      >
        <line x1="0" x2={w} y1={h - 14} y2={h - 14} className="stroke-white/10" />
        {points.map((p, i) => {
          const x = 4 + i * step
          let base = 0
          return (
            <g key={p.date}>
              <title>{`${stamp(p.date)}: ${p.populated} fields (${p.package} package, ${p.calculated} calculated, ${p.person} person)`}</title>
              {ACTOR_SWATCH.map((a, j) => {
                const n = p[a.key]
                const top = y(base + n)
                const rect = <rect key={j} x={x} y={top} width={barW} height={Math.max(0, y(base) - top)} className={a.svg} />
                base += n
                return rect
              })}
            </g>
          )
        })}
        <text x="4" y={h - 2} className="fill-stone-500 text-[9px]">{stamp(first.date)}</text>
        <text x={w - 4} y={h - 2} textAnchor="end" className="fill-stone-500 text-[9px]">{stamp(last.date)}</text>
        <text x={w - 4} y="9" textAnchor="end" className="fill-stone-500 text-[9px]">{max} fields</text>
      </svg>
      {legend}
      <p className="text-[10px] leading-snug text-stone-600" title={NOT_THE_SCORE}>Recorded events only, not the score.</p>
    </figure>
  )
}

function whereFound(f: NonNullable<FieldMapRow['found']>[number]): string {
  const file = f.document ?? 'the package'
  if (f.sheet) return `${file} · ${f.sheet}!${f.cell ?? ''}`
  if (f.line) return `${file} · line ${f.line}${f.page ? `, page ${f.page}` : ''}`
  return file
}

function Label({ children }: { children: ReactNode }) {
  return <div className="mb-0.5 font-mono text-[9px] uppercase tracking-wider text-stone-600">{children}</div>
}

/** A label over its value, so the value gets the column's whole width. */
function Fact({ label, children, tone = 'text-stone-300' }: { label: string; children: ReactNode; tone?: string }) {
  return (
    <div className="min-w-0">
      <dt className="font-mono text-[9px] uppercase tracking-wider text-stone-600">{label}</dt>
      <dd className={tone}>{children}</dd>
    </div>
  )
}

/** The detail beneath a field's row: how it got there, or what it needs. */
export function FieldDetail({ row }: { row: FieldMapRow }) {
  if (row.state === 'populated') {
    const o = row.origin
    return (
      <dl className="grid gap-x-6 gap-y-1.5 text-[11px] sm:grid-cols-3">
        <Fact label="Held on">{row.heldOn}</Fact>
        {row.ownership?.stamp ? (
          <Fact label="Whose term" tone={row.ownership.awaitingDeskTerms ? 'text-amber-200' : 'text-stone-300'}>
            {row.ownership.stamp}{row.ownership.note ? ` — ${row.ownership.note}` : ''}
          </Fact>
        ) : null}
        {o ? (
          <>
            <Fact label="Origin">{ACTOR_LABEL[o.actor]}{o.source ? ` · ${o.source}` : ''}</Fact>
            <Fact label="Recorded">{stamp(o.at)} · {o.state}{o.engineVersion ? ` · engine ${o.engineVersion.slice(0, 8)}` : ''}</Fact>
          </>
        ) : (
          <Fact label="Origin" tone="text-stone-400">No event was recorded for this field; it is held on the {row.heldOn}.</Fact>
        )}
      </dl>
    )
  }
  const impact = row.impact
  return (
    <div className="grid gap-x-8 gap-y-3 text-[11px] lg:grid-cols-2">
      <div className="min-w-0 space-y-2">
        <dl className="space-y-1.5">
          {row.assumed ? <Fact label="Default applied" tone="text-amber-200">{row.assumed.basis}</Fact> : null}
          <Fact label="Why it is blank">{row.why || RESOLUTION_LABEL[row.resolution ?? 'unavailable']}</Fact>
          {row.need ? <Fact label="What is needed" tone="text-stone-200">{row.need}</Fact> : null}
          {row.ownership ? <Fact label="Whose term">{row.ownership.note}</Fact> : null}
        </dl>
        {row.found?.length ? (
          <div>
            <Label>Stated in the package</Label>
            <ul className="space-y-0.5">
              {row.found.map((f, i) => (
                <li key={i} className="text-stone-300">
                  <span className="font-medium text-stone-100">{formatFieldValue(f.value, row.kind, row.field) || String(f.value)}</span>
                  <span className="text-stone-500"> — {whereFound(f)}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {row.searched?.length ? (
          <div className="text-stone-500">
            Searched: {row.searched.map((s) => `${s.document ?? 'a source'} (${s.outcome.replace(/_/g, ' ')})`).join(' · ')}
          </div>
        ) : null}
      </div>
      {impact ? (
        <div className="min-w-0">
          <Label>What its absence costs</Label>
          <ul className="space-y-0.5 text-stone-300">
            {impact.requiredAtIntake ? <li>Required at intake: a person cannot create the deal without it.</li> : null}
            {impact.creditScreenInput ? <li>An input to the credit-box screen: the screen reads it, and re-runs when it changes.</li> : null}
            {impact.pipelineCritical ? <li>Pipeline-critical: its absence shows as a gap on the pipeline row.</li> : null}
            {impact.reviewDatapoint ? <li>On the review checklist.</li> : null}
            {impact.blocks.map((b) => (
              <li key={b.target}>
                Holds back <span className="text-stone-100">{b.label}</span> ({b.formula})
                {b.alsoNeeds.length ? <span className="text-stone-500"> — which also needs {b.alsoNeeds.join(', ')}</span> : null}
              </li>
            ))}
            {!impact.requiredAtIntake && !impact.creditScreenInput && !impact.pipelineCritical && !impact.reviewDatapoint && impact.blocks.length === 0
              ? <li className="text-stone-500">Nothing else on the platform waits on it; it is a detail field.</li>
              : null}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
